import { usePublishedMediaInsightsDcrDataLabDataQuery } from "@decentriq/graphql/dist/hooks";
import { useQuery } from "@tanstack/react-query";
import { memo, useMemo } from "react";
import { useApiCore } from "contexts";
import { MediaDataRoomJobInput } from "features/mediaDataRoom/hooks/ab";
import { MediaDataRoomVersion } from "features/mediaDataRoom/models";
import { useMediaDataRoom } from "../../MediaDataRoomContext";
import {
  type MediaDataRoomInsightsDataAbContextValue,
  MediaDataRoomInsightsDataContext,
} from "../models";
import useAdvertiserDataReport from "./useAdvertiserDataReport";
import useDatasets from "./useDatasets";
import useInsights from "./useInsights";
import useOverlapStatistics from "./useOverlapStatistics";
import usePublisherDataReport from "./usePublisherDataReport";
import useScopeId from "./useScopeId";

export type MediaDataRoomInsightsDataAbWrapperProps = React.PropsWithChildren;

const MediaDataRoomInsightsDataAbWrapper =
  memo<MediaDataRoomInsightsDataAbWrapperProps>(({ children }) => {
    const {
      dataRoomId,
      driverAttestationHash,
      isDeactivated,
      isAdvertiser,
      isDataPartner,
      isPublisher,
      hasDataPartner,
      supportedFeatures: { enableInsights, enableDropInvalidRows },
    } = useMediaDataRoom(MediaDataRoomVersion.ab);
    const { sessionManager, client } = useApiCore();
    const {
      data: dataLabData,
      loading: dataLabDataLoading,
      refetch: refetchDatalabQuery,
    } = usePublishedMediaInsightsDcrDataLabDataQuery({
      skip: !isPublisher,
      variables: {
        id: dataRoomId,
      },
    });
    const { data: session } = useQuery({
      enabled: driverAttestationHash != null,
      queryFn: async () => {
        const session = await sessionManager.get({ driverAttestationHash });
        resetScopeId(await client.ensureDcrDataScope(dataRoomId));
        return session;
      },
      queryKey: [
        ...MediaDataRoomJobInput.buildQueryPrefix({
          dataRoomId,
          driverAttestationHash,
        }),
        "session",
        "fetchResults",
      ],
      refetchOnMount: "always",
    });
    const { getScopeId, resetScopeId } = useScopeId({ dataRoomId, session });
    const datasets = useDatasets({
      dataRoomId,
      driverAttestationHash,
      refetchDataLab: isPublisher ? refetchDatalabQuery : undefined,
      session,
    });
    const insights = useInsights({
      dataRoomId,
      driverAttestationHash,
      getScopeId,
      publishedDatasetsHashes: datasets.data,
      session,
      skip:
        isDeactivated ||
        !datasets.data.hasRequiredData ||
        !enableInsights ||
        isDataPartner,
    });
    const overlapStatistics = useOverlapStatistics({
      dataRoomId,
      driverAttestationHash,
      getScopeId,
      publishedDatasetsHashes: datasets.data,
      session,
      skip: isDeactivated || !datasets.data.hasRequiredData,
    });
    const advertiserDataReport = useAdvertiserDataReport({
      dataRoomId,
      driverAttestationHash,
      getScopeId,
      publishedDatasetsHashes: datasets.data,
      session,
      skip:
        isDeactivated ||
        !(hasDataPartner ? isDataPartner : isAdvertiser) ||
        !datasets.data.hasAdvertiserData,
    });
    const publisherDataReport = usePublisherDataReport({
      dataRoomId,
      driverAttestationHash,
      getScopeId,
      publishedDatasetsHashes: datasets.data,
      session,
      skip: isDeactivated || !isPublisher || !enableDropInvalidRows,
    });
    const contextValue = useMemo<MediaDataRoomInsightsDataAbContextValue>(
      () => ({
        advertiserDataReport,
        dataLabDataLoading,
        datasets,
        getScopeId,
        insights,
        isPublisherAudienceBeingUpdated:
          Boolean(
            dataLabData?.publishedMediaInsightsDcr?.wasDataLabPublishedBefore
          ) &&
          !datasets.data.hasPublisherData &&
          Boolean(datasets.data.hasAdvertiserData),
        overlapStatistics,
        publishedDataLab:
          dataLabData?.publishedMediaInsightsDcr?.publishedDataLab ?? null,
        publishedDatasetsHashes: datasets.data,
        publisherDataReport,
        resetScopeId,
        session,
        version: MediaDataRoomVersion.ab,
      }),
      [
        advertiserDataReport,
        dataLabDataLoading,
        datasets,
        getScopeId,
        insights,
        dataLabData?.publishedMediaInsightsDcr?.wasDataLabPublishedBefore,
        dataLabData?.publishedMediaInsightsDcr?.publishedDataLab,
        overlapStatistics,
        publisherDataReport,
        resetScopeId,
        session,
      ]
    );
    return (
      <MediaDataRoomInsightsDataContext.Provider value={contextValue}>
        {children}
      </MediaDataRoomInsightsDataContext.Provider>
    );
  });

MediaDataRoomInsightsDataAbWrapper.displayName =
  "MediaDataRoomInsightsDataAbWrapper";

export default MediaDataRoomInsightsDataAbWrapper;
